/*

Fetch calls always make us check for response.ok and need special
handling of the 400 errors coming back from the server.
This is to try to make it easier to handle these.

Code using this helper should look like this:

wrapFetch(fetch('/some-url'))
  .onSuccess(json => {
    // yay!
  })
  .onBadRequest((err, json) => {
    // err will be a multi-line message. json will be the error json body returned
  })
  .onStatus(413, () => {
    // handle request too large
  })
  .catch(err => {
    // something unexpected
  });

*/

import { COGNITO_AUTHORITY, COGNITO_CLIENT_ID } from '../utils/Constants'

function wrapFetch(promise) {
  let onSuccessHandler = response => response
  let onBadRequestHandler

  const onStatusHandlers = {}

  const promise2 = promise.then(async response => {
    if (onStatusHandlers[response.status]) {
      return onStatusHandlers[response.status](response)
    }

    if (response.ok) {
      if (response.status === 204) {
        if (onSuccessHandler) return onSuccessHandler(null)
        return null
      }
      const text = await response.text()
      const json = text ? JSON.parse(text) : {}
      if (onSuccessHandler) return onSuccessHandler(json)
    } else if (response.status === 400) {
      // https://docs.microsoft.com/en-us/aspnet/core/web-api/?view=aspnetcore-3.0#default-badrequest-response
      const json = await response.json()
      console.log('Smart Review Error JSON:', json)
      const errorMsg = convertApiBadRequestBodyToMessage(json)
      if (onBadRequestHandler) {
        return onBadRequestHandler(errorMsg, json)
      } else {
        const error = new Error(errorMsg || 'Bad request')
        error.response = response
        throw error
      }
    } else {
      // Get response body for better error details
      const responseBody = await response.text().catch(() => 'Unable to read response body')
      const url = response.url || 'unknown URL'

      // Create specific error messages based on status code
      let errorMessage = ''
      switch (response.status) {
        case 401: {
          localStorage.removeItem(`oidc.user:${COGNITO_AUTHORITY}:${COGNITO_CLIENT_ID}`)
          const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
          window.location.replace(`/signin?returnTo=${returnTo}`)
          return new Promise(() => {})
        }
        case 403:
          errorMessage = `Access forbidden (403) accessing ${url}. You don't have permission to access this resource.`
          break
        case 404:
          errorMessage = `Resource not found (404) at ${url}.`
          break
        case 500:
          errorMessage = `Internal server error (500) at ${url}. Please try again later.`
          break
        default:
          errorMessage = `HTTP ${response.status} ${response.statusText} accessing ${url}.`
      }

      // Add response body if available
      if (responseBody && responseBody.trim()) {
        errorMessage += ` Response: ${responseBody}`
      }

      const error = new Error(errorMessage)
      error.response = response
      error.status = response.status
      throw error
    }
  })

  promise2.onSuccess = handler => {
    onSuccessHandler = handler
    return promise2
  }
  promise2.onBadRequest = handler => {
    onBadRequestHandler = handler
    return promise2
  }

  const registerHandlerForStatus = (status, handler) => {
    onStatusHandlers[status] = handler
    return promise2
  }

  promise2.onStatus = registerHandlerForStatus

  return promise2
}

function formatValidationErrorMessage(msg) {
  if (!msg) return msg
  if (typeof msg !== 'string') return JSON.stringify(msg)

  if (msg.includes('status.history') || msg.includes('status/history') || msg.includes('status › history')) {
    return 'Work Status entry is incomplete — please fill in all required fields or remove the entry.'
  }
  if (msg.includes('mmi.history') || msg.includes('mmi/history') || msg.includes('mmi › history')) {
    return 'MMI entry is incomplete — please fill in all required fields or remove the entry.'
  }
  if (msg.includes('NoTypeValidates')) {
    return 'One or more entries are incomplete — please fill in all required fields or remove empty entries.'
  }
  return msg
}

function convertApiBadRequestBodyToMessage(json) {
  if (!json) return 'Bad request'

  const errorsObj = json.errors || json.Errors || json.validationErrors || {}
  const details = []

  Object.keys(errorsObj).forEach(prop => {
    const val = errorsObj[prop]
    if (Array.isArray(val)) {
      val.forEach(error => {
        const formatted = formatValidationErrorMessage(error)
        if (formatted) details.push(formatted)
      })
    } else if (typeof val === 'string') {
      const formatted = formatValidationErrorMessage(val)
      if (formatted) details.push(formatted)
    } else if (val) {
      details.push(JSON.stringify(val))
    }
  })

  if (details.length > 0) {
    return Array.from(new Set(details)).join('\n')
  } else if (json.message || json.Message) {
    return formatValidationErrorMessage(json.message || json.Message)
  } else if (json.errors && Array.isArray(json.errors)) {
    return Array.from(new Set(json.errors.map(formatValidationErrorMessage))).join('\n')
  }

  return json.title || 'Request is not valid'
}

export { wrapFetch }
